// Banned Agents — the club settings page for the SILENT agent ban.
//
// Work order (TP_PITCH_LIFECYCLE.md, PART A, "The real ban"):
//   "Mandatory: the 'Banned Agents' management menu — a menu entry in the club
//    settings area where 'Notifications and Permissions' live. Access: every
//    club member who has access to Club Tenders — the same permission gates
//    this menu. Complete flows, nothing half-built: list of banned agents with
//    scope (this request — with a reference to it — or whole transfer window),
//    banned date and banned-by; unban per entry with confirm dialog, effect
//    immediate (agent sees the requests again); clean empty state ('No banned
//    agents')."
//
// The permission is `view_tenders` — the codename that gates Club Tenders
// itself (routes/index.js: /club/tenders), read out of the code rather than
// invented. The route carries the same gate; the backend enforces it again
// through HasClubPermission.
//
// SILENCE IS PART OF THE PRODUCT, so this page says so out loud in its
// subtitle: the club should know the agent is never told. Nothing on this
// screen sends anything to anybody — ban and unban are plain rows.
//
// Layout copies pages/settings/SecuritySettings.js (the platform's existing
// settings-list idiom: #F6F8FB shell, white card, divided <ul> of rows,
// per-row destructive action, empty state). No table, so nothing scrolls
// sideways at phone width; the row meta collapses to one column below `sm`.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

import ConfirmationModal from "../../components/club/ConfirmationModal";
import useTranslate from "../../hooks/useTranslate";
import { bannedAgentService } from "../../services/api";

const ACCENT = "#2283FF";
const BORDER = "#E5E7EB";

const LONG_DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return LONG_DATE.format(d);
}

const BannedAgents = () => {
  const { t, tv } = useTranslate();
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [lifting, setLifting] = useState(false);
  const abortRef = useRef(null);

  const fetchBans = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const res = await bannedAgentService.list({ signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      if (res.success) {
        setBans(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.error || t("Failed to load banned agents."));
      }
    } catch (e) {
      if (e?.name !== "CanceledError") {
        setError(t("Failed to load banned agents."));
      }
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBans();
    return () => abortRef.current?.abort();
  }, [fetchBans]);

  const handleUnban = async () => {
    if (!confirmTarget || lifting) return;
    setLifting(true);
    const res = await bannedAgentService.unban(confirmTarget.id);
    if (res.success) {
      toast.success(t("Ban lifted."));
      // The backend effect is immediate, so the row leaves the list at once
      // rather than after a refetch.
      setBans((prev) => prev.filter((b) => b.id !== confirmTarget.id));
      setConfirmTarget(null);
    } else {
      toast.error(res.error || t("Failed to lift this ban."));
    }
    setLifting(false);
  };

  // "this request — with a reference to it — or whole transfer window".
  const scopeLine = (ban) => {
    if (ban.scope === "request") {
      if (!ban.request) return t("One request (no longer available)");
      return (
        <>
          {t("This request")}
          {" · "}
          <Link
            to={`/club/tenders/${ban.request.id}/pitches`}
            className="font-medium hover:underline"
            style={{ color: ACCENT }}
          >
            {tv("Request #{{id}}", { id: ban.request.id })}
          </Link>
        </>
      );
    }
    return t("Whole transfer window");
  };

  // An honest second line for the window scope: a real frozen end date when the
  // club's transfer-window data gave us one, and the plain truth when it did
  // not — never an invented date.
  const scopeDetail = (ban) => {
    if (ban.scope !== "transfer_window") return null;
    if (ban.banned_until) {
      return tv("Ends {{date}}", { date: formatDate(ban.banned_until) });
    }
    return t("No end date — until you lift it");
  };

  const hasBans = bans.length > 0;

  return (
    <div className="min-h-screen bg-[#F6F8FB] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[#0F172A]">
            {t("Banned Agents")}
          </h1>
          <p className="text-sm text-[#475569] mt-1">
            {t(
              "Agents you banned no longer see the requests below and cannot send new proposals to them. They are not notified.",
            )}
          </p>
        </header>

        <section
          className="bg-white rounded-xl shadow-sm border"
          style={{ borderColor: BORDER, borderRadius: 12 }}
        >
          <div className="px-5 py-4 border-b" style={{ borderColor: BORDER }}>
            <h2 className="text-lg font-semibold text-[#0F172A]">
              {t("Active bans")}
            </h2>
            <p className="text-sm text-[#475569] mt-0.5">
              {t(
                "Proposals an agent already sent you are not affected — they run their normal course.",
              )}
            </p>
          </div>

          {loading && (
            <div className="p-8 text-center text-[#94A3B8] text-sm">
              {t("Loading banned agents…")}
            </div>
          )}

          {!loading && error && (
            <div className="p-8 text-center text-[#EF4444] text-sm">
              {error}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={fetchBans}
                  className="text-[#2283FF] hover:underline"
                >
                  {t("Retry")}
                </button>
              </div>
            </div>
          )}

          {!loading && !error && !hasBans && (
            <div className="p-8 text-center text-[#94A3B8] text-sm">
              <div className="text-base text-[#475569] mb-1">
                {t("No banned agents")}
              </div>
              {t(
                "You have not banned anyone. You can ban an agent from a proposal on one of your requests.",
              )}
            </div>
          )}

          {!loading && !error && hasBans && (
            <ul className="divide-y" style={{ borderColor: BORDER }}>
              {bans.map((ban) => (
                <li
                  key={ban.id}
                  className="px-5 py-4 flex items-start gap-3 sm:gap-4"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: "#FEF2F2", color: "#EF4444" }}
                    aria-hidden="true"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      />
                      <path
                        d="M5.6 5.6l12.8 12.8"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0F172A] break-words">
                      {ban.agent?.name || t("Unknown agent")}
                    </div>
                    {ban.agent?.agency_name && (
                      <div className="text-xs text-[#94A3B8] break-words">
                        {ban.agent.agency_name}
                      </div>
                    )}

                    <div className="text-xs text-[#475569] mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                      <span className="break-words">
                        {t("Scope")}:{" "}
                        <span className="font-medium text-[#0F172A]">
                          {scopeLine(ban)}
                        </span>
                      </span>
                      <span>
                        {t("Banned on")}:{" "}
                        <span className="font-medium text-[#0F172A]">
                          {formatDate(ban.banned_at)}
                        </span>
                      </span>
                      <span className="break-words">
                        {t("Banned by")}:{" "}
                        <span className="font-medium text-[#0F172A]">
                          {ban.banned_by_name || "—"}
                        </span>
                      </span>
                      {scopeDetail(ban) && (
                        <span className="break-words">
                          <span className="font-medium text-[#0F172A]">
                            {scopeDetail(ban)}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setConfirmTarget(ban)}
                    disabled={lifting}
                    className="text-sm font-semibold text-[#2283FF] hover:bg-[#EBF5FF] px-3 py-2 rounded-md flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("Unban")}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!loading && !error && hasBans && (
            <div
              className="px-5 py-3 border-t text-xs text-[#94A3B8]"
              style={{ borderColor: BORDER }}
            >
              {bans.length === 1
                ? t("1 active ban")
                : tv("{{count}} active bans", { count: bans.length })}
            </div>
          )}
        </section>
      </div>

      <ConfirmationModal
        isOpen={!!confirmTarget}
        onClose={() => (lifting ? null : setConfirmTarget(null))}
        onConfirm={handleUnban}
        loading={lifting}
        variant="v2"
        title={t("Unban agent")}
        message={
          confirmTarget
            ? tv(
                "Unban {{name}}? They will see your requests again and can send new proposals.",
                { name: confirmTarget.agent?.name || t("this agent") },
              )
            : ""
        }
        confirmText={t("Unban")}
        confirmButtonClass="bg-[#2283FF] hover:bg-[#1b6fd8]"
      />
    </div>
  );
};

export default BannedAgents;
